import { apiGet, apiPost } from "./client";

export type ReviewChargeProbeStatus = {
  probe: "status";
  available: true;
  gates: {
    readEnabled: boolean;
    writesEnabled: boolean;
    chargeExecutionEnabled: boolean;
  };
};

export type ReviewChargeProbeResult = {
  probe: "chargeGateDisabled" | "paidReplay";
  passed: true;
  observedCode?: "STUDENT_BILLING_CHARGE_EXECUTION_DISABLED";
  execution?: {
    executionId: string;
    status: "paid";
    attemptCount: number;
    amount: number;
    payerId: string;
  };
  evidence: {
    billingUnitStatus: string | null;
    executionCount: number;
    attemptCount: number;
    paymentAuditCount: number;
  };
};

function record(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error(`Invalid ${label}`);
  return value as Record<string, unknown>;
}

function exact(value: Record<string, unknown>, keys: readonly string[], label: string): void {
  if (Object.keys(value).some((key) => !keys.includes(key))) throw new Error(`Invalid ${label}`);
}

function parseStatus(value: unknown): ReviewChargeProbeStatus {
  const root = record(value, "Review charge probe status");
  exact(root, ["probe", "available", "gates"], "Review charge probe status");
  const gates = record(root.gates, "Review charge probe gates");
  exact(gates, ["readEnabled", "writesEnabled", "chargeExecutionEnabled"], "Review charge probe gates");
  if (
    root.probe !== "status" || root.available !== true
    || typeof gates.readEnabled !== "boolean"
    || typeof gates.writesEnabled !== "boolean"
    || typeof gates.chargeExecutionEnabled !== "boolean"
  ) throw new Error("Invalid Review charge probe status");
  return {
    probe: "status",
    available: true,
    gates: {
      readEnabled: gates.readEnabled,
      writesEnabled: gates.writesEnabled,
      chargeExecutionEnabled: gates.chargeExecutionEnabled
    }
  };
}

function parseResult(value: unknown): ReviewChargeProbeResult {
  const root = record(value, "Review charge probe result");
  exact(root, ["probe", "passed", "observedCode", "execution", "evidence"], "Review charge probe result");
  if ((root.probe !== "chargeGateDisabled" && root.probe !== "paidReplay") || root.passed !== true) {
    throw new Error("Invalid Review charge probe result");
  }
  const evidence = record(root.evidence, "Review charge probe evidence");
  exact(evidence, ["billingUnitStatus", "executionCount", "attemptCount", "paymentAuditCount"], "Review charge probe evidence");
  if (
    (evidence.billingUnitStatus !== null && typeof evidence.billingUnitStatus !== "string")
    || !Number.isSafeInteger(evidence.executionCount)
    || !Number.isSafeInteger(evidence.attemptCount)
    || !Number.isSafeInteger(evidence.paymentAuditCount)
  ) throw new Error("Invalid Review charge probe evidence");
  const result: ReviewChargeProbeResult = {
    probe: root.probe,
    passed: true,
    evidence: {
      billingUnitStatus: evidence.billingUnitStatus as string | null,
      executionCount: evidence.executionCount as number,
      attemptCount: evidence.attemptCount as number,
      paymentAuditCount: evidence.paymentAuditCount as number
    }
  };
  if (root.probe === "chargeGateDisabled") {
    if (root.observedCode !== "STUDENT_BILLING_CHARGE_EXECUTION_DISABLED" || root.execution !== undefined) {
      throw new Error("Invalid Review charge-disabled probe result");
    }
    result.observedCode = root.observedCode;
  } else {
    const execution = record(root.execution, "Review paid replay execution");
    exact(execution, ["executionId", "status", "attemptCount", "amount", "payerId"], "Review paid replay execution");
    if (
      typeof execution.executionId !== "string" || execution.status !== "paid"
      || !Number.isSafeInteger(execution.attemptCount) || !Number.isSafeInteger(execution.amount)
      || typeof execution.payerId !== "string" || root.observedCode !== undefined
    ) throw new Error("Invalid Review paid replay execution");
    result.execution = {
      executionId: execution.executionId,
      status: "paid",
      attemptCount: execution.attemptCount as number,
      amount: execution.amount as number,
      payerId: execution.payerId
    };
  }
  return result;
}

function base(tenantSlug: string): string {
  return `/api/v1/review/debug/tenants/${encodeURIComponent(tenantSlug)}/student-billing-charge-probe`;
}

export async function fetchReviewChargeProbeStatus(tenantSlug: string): Promise<ReviewChargeProbeStatus> {
  return parseStatus(await apiGet(`${base(tenantSlug)}/status`));
}

export async function runReviewChargeGateDisabledProbe(tenantSlug: string, billingUnitId: string): Promise<ReviewChargeProbeResult> {
  return parseResult(await apiPost(`${base(tenantSlug)}/gate-disabled`, { billingUnitId }));
}

export async function runReviewPaidReplayProbe(
  tenantSlug: string,
  billingUnitId: string,
  expectedExecutionId: string
): Promise<ReviewChargeProbeResult> {
  return parseResult(await apiPost(`${base(tenantSlug)}/paid-replay`, { billingUnitId, expectedExecutionId }));
}
